import { FC, ReactElement, useState } from 'react'

import { LoadingSpinner } from '../../components'
import {
    CalendarControls,
    EmptyState,
    ModalContent,
    PacoModal,
} from '../../compositions'
import { ConnectedValkTalkPostForm } from '../../connectors/@forms/ConnectedValkTalkPostForm/ConnectedValkTalkPostForm'
import { ValkTalkFeedType, ValkTalkPost } from '../../entities/ValkTalkPost/ValkTalkPost'
import { getValkTalkPostDisplayTitle } from '../../entities/ValkTalkPost/ValkTalkPostHelpers'
import { addMonths, formatDate, startOfMonth } from '../../helpers/date'
import trans from '../../helpers/trans'
import { TimeModeType } from '../../types'
import { DateFormat } from '../../types/dateFormatTypes'
import ConfirmForm from '../@forms/ConfirmForm/ConfirmForm'
import { ValkTalkPostsTable } from '../@tables/ValkTalkPostsTable/ValkTalkPostsTable'
import UserSearch from '../UserSearch/UserSearch'

import './ValkTalkPosts.scss'

interface ValkTalkPostsProps {
    isLoading: boolean
    isDeleting: boolean
    isAddPostModalOpen: boolean
    hasMorePosts: boolean
    month: Date
    search: string
    posts: ValkTalkPost[]
    permittedFeedTypes: ValkTalkFeedType[]
    onMonthChange: (month: Date) => void
    onSearchChange: (search: string) => void
    onAddPostModalClose: () => void
    onPostSaved: () => void
    onDeletePost: (post: ValkTalkPost) => Promise<boolean>
    className?: string
}

export const ValkTalkPosts: FC<ValkTalkPostsProps> = ({
    isLoading,
    isDeleting,
    isAddPostModalOpen,
    hasMorePosts,
    month,
    search,
    posts,
    permittedFeedTypes,
    onMonthChange,
    onSearchChange,
    onAddPostModalClose,
    onPostSaved,
    onDeletePost,
    className = '',
}): ReactElement => {
    const [postToEdit, setPostToEdit] = useState<ValkTalkPost>()
    const [postToDelete, setPostToDelete] = useState<ValkTalkPost>()

    const handlePostFormModalClose = (): void => {
        setPostToEdit(undefined)
        onAddPostModalClose()
    }

    const handlePostSaved = (): void => {
        handlePostFormModalClose()
        onPostSaved()
    }

    const handleDeleteConfirm = async (): Promise<void> => {
        if (postToDelete && await onDeletePost(postToDelete)) {
            setPostToDelete(undefined)
        }
    }

    const formattedMonth = formatDate(month, DateFormat.monthYear)

    return (
        <div className={`valk-talk-posts ${className}`}>
            <div className="valk-talk-posts__top-bar">
                <h1 className="valk-talk-posts__title">{trans('containers.valkTalkPosts.title')}</h1>

                <CalendarControls
                    inputType={TimeModeType.month}
                    timeMode={TimeModeType.month}
                    dateFormat={DateFormat.monthYear}
                    selectedDays={[month]}
                    onDateChange={date => onMonthChange(startOfMonth(date))}
                    onIncrementClick={goForward => onMonthChange(addMonths(month, goForward ? 1 : -1))}
                    onTodayButtonClick={() => onMonthChange(startOfMonth(new Date()))}
                    className="valk-talk-posts__calendar-controls"
                />

                <UserSearch
                    label={trans('containers.valkTalkPosts.searchPosts')}
                    search={search}
                    onSearchChange={onSearchChange}
                    className="valk-talk-posts__search"
                />
            </div>

            {isLoading && ! posts.length && <LoadingSpinner />}

            {! isLoading && ! posts.length && (
                <EmptyState
                    title={trans('containers.valkTalkPosts.nullState.title')}
                    intro={trans('containers.valkTalkPosts.nullState.description', { month: formattedMonth })}
                    className="valk-talk-posts__empty-state"
                />
            )}

            {!! posts.length && (
                <div className="valk-talk-posts__table-wrapper">
                    {isLoading && <LoadingSpinner isSmall className="valk-talk-posts__loader" />}

                    <ValkTalkPostsTable
                        posts={posts}
                        permittedFeedTypes={permittedFeedTypes}
                        onEditPostClick={setPostToEdit}
                        onDeletePostClick={setPostToDelete}
                        className="valk-talk-posts__table"
                    />
                </div>
            )}

            {hasMorePosts && (
                <p className="valk-talk-posts__limit-notice">
                    {trans('containers.valkTalkPosts.limitNotice')}
                </p>
            )}

            {(isAddPostModalOpen || postToEdit) && (
                <PacoModal keyboardDismiss={false} onClose={handlePostFormModalClose}>
                    <ModalContent
                        title={trans(postToEdit ? 'containers.valkTalkPosts.editPost' : 'containers.valkTalkPosts.addPost')}
                    >
                        <ConnectedValkTalkPostForm
                            post={postToEdit}
                            onSaved={handlePostSaved}
                            onClose={handlePostFormModalClose}
                        />
                    </ModalContent>
                </PacoModal>
            )}

            {postToDelete && (
                <PacoModal onClose={() => setPostToDelete(undefined)}>
                    <ModalContent title={trans('containers.valkTalkPosts.deletePost')}>
                        <ConfirmForm
                            isLoading={isDeleting}
                            intro={trans('containers.valkTalkPosts.deletePostIntro', {
                                title: getValkTalkPostDisplayTitle(postToDelete, 40)
                                    || trans('containers.valkTalkPosts.thisPost'),
                            })}
                            submitText={trans('common.delete')}
                            onCancel={() => setPostToDelete(undefined)}
                            onSubmit={handleDeleteConfirm}
                        />
                    </ModalContent>
                </PacoModal>
            )}
        </div>
    )
}
